import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { MapPin, Building2, ArrowRight, Sparkles, PlusCircle } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [sites, setSites] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [sitesRes, usageRes] = await Promise.all([
          api.get("/sites"),
          api.get("/usage").catch(() => null),
        ]);
        setSites(sitesRes.data.sites);
        if (usageRes) setUsage(usageRes.data);
      } catch (_e) {
        // Silent — sidebar nav still works
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const queries = usage?.queries;
  const queriesValue = loading
    ? "—"
    : queries?.unlimited === false
      ? `${queries.used} / ${queries.limit}`
      : "∞";

  return (
    <div className="max-w-6xl mx-auto" data-testid="dashboard-page">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-[#5C6B61] font-medium mb-2">{t("dashboard.subtitle")}</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#1A2F24]">
          {t("dashboard.welcome")}, {user?.name?.split(" ")[0] || ""}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <KpiCard label={t("dashboard.kpi_sites")} value={loading ? "—" : sites.length} icon={Building2} />
        <KpiCard label={t("dashboard.kpi_plan")} value={(user?.subscription_plan || "free").toUpperCase()} icon={Sparkles} accent />
        <KpiCard
          label={t("dashboard.kpi_queries")}
          value={queriesValue}
          icon={MapPin}
          hint={queries?.unlimited === false ? t("dashboard.quota_left", { n: queries.remaining }) : null}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <ActionCard
          to="/app/query"
          title={t("dashboard.cta_query")}
          icon={MapPin}
          testid="action-new-query"
          primary
        />
        <ActionCard
          to="/app/sites"
          title={t("dashboard.cta_sites")}
          icon={Building2}
          testid="action-all-sites"
        />
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-semibold text-xl text-[#1A2F24]">{t("dashboard.recent")}</h2>
          <Link to="/app/sites" className="text-sm text-[#1A2F24] hover:underline">→</Link>
        </div>
        {loading ? (
          <div className="text-[#5C6B61] text-sm">{t("common.loading")}</div>
        ) : sites.length === 0 ? (
          <div className="py-10 text-center">
            <div className="text-[#5C6B61] mb-4">{t("dashboard.no_sites")}</div>
            <Link to="/app/query">
              <Button data-testid="dashboard-create-first" className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white">
                <PlusCircle size={16} className="mr-2" /> {t("dashboard.create_first")}
              </Button>
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-[#E5E2D9]">
            {sites.slice(0, 5).map((s) => (
              <li key={s.id} className="py-3 flex items-center justify-between" data-testid={`dashboard-site-${s.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-[#1A2F24] truncate">{s.name}</div>
                  <div className="text-xs text-[#5C6B61] truncate">{s.address || `${s.latitude?.toFixed(4)}, ${s.longitude?.toFixed(4)}`}</div>
                </div>
                <Link to={`/app/sites/${s.id}`} className="text-sm text-[#1A2F24] hover:underline whitespace-nowrap">
                  {t("sites.view")} →
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, accent = false, hint = null }) {
  return (
    <div className={`rounded-xl border p-6 ${accent ? "bg-[#1A2F24] text-white border-[#1A2F24]" : "bg-white border-[#E5E2D9]"}`}>
      <div className="flex items-start justify-between mb-3">
        <span className={`text-xs uppercase tracking-wide ${accent ? "text-white/60" : "text-[#5C6B61]"}`}>{label}</span>
        <Icon size={18} className={accent ? "text-white/70" : "text-[#5C6B61]"} />
      </div>
      <div className={`text-3xl font-display font-bold ${accent ? "text-white" : "text-[#1A2F24]"}`}>{value}</div>
      {hint && (
        <div className={`text-xs mt-1.5 ${accent ? "text-white/60" : "text-[#5C6B61]"}`}>{hint}</div>
      )}
    </div>
  );
}

function ActionCard({ to, title, icon: Icon, testid, primary = false }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group rounded-xl border p-6 flex items-center justify-between transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
        primary
          ? "bg-[#C05A44] text-white border-[#C05A44] hover:bg-[#A64B38]"
          : "bg-white border-[#E5E2D9] hover:border-[#1A2F24]"
      }`}
    >
      <span className="flex items-center gap-4">
        <span className={`flex items-center justify-center w-12 h-12 rounded-lg ${primary ? "bg-white/20" : "bg-[#F1EFE7]"}`}>
          <Icon size={22} />
        </span>
        <span className="font-display font-semibold text-lg">{title}</span>
      </span>
      <ArrowRight size={20} className="opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
    </Link>
  );
}
